import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  User, Mic2, ClipboardList, CalendarCheck, Shield, MessageCircle,
  Eye, Save, RotateCcw, Check, Lock, AlertTriangle, Play, Globe, Brain,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { IndustryBrainSection } from '../components/IndustryBrain';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'industry', label: 'Industry Brain', icon: Brain },
  { id: 'identity', label: 'Identity', icon: User },
  { id: 'voice', label: 'Voice & Tone', icon: Mic2 },
  { id: 'language', label: 'Languages', icon: Globe },
  { id: 'qualification', label: 'Lead Qualification', icon: ClipboardList },
  { id: 'booking', label: 'Booking Rules', icon: CalendarCheck },
  { id: 'objection', label: 'Objection Handling', icon: MessageCircle },
  { id: 'escalation', label: 'Escalation & Compliance', icon: Shield },
  { id: 'preview', label: 'Preview / Test', icon: Eye },
];

export default function AssistantSettingsPage() {
  const { token } = useAuth();
  const [tab, setTab] = useState('industry');
  const [settings, setSettings] = useState(null);
  const [locked, setLocked] = useState([]);
  const [langSettings, setLangSettings] = useState(null);
  const [availableLangs, setAvailableLangs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const hdrs = { Authorization: `Bearer ${token}` };

  const fetchSettings = useCallback(async () => {
    try {
      const [settingsRes, langRes] = await Promise.all([
        axios.get(`${API}/assistant-settings`, { headers: hdrs }),
        axios.get(`${API}/language-settings`, { headers: hdrs }),
      ]);
      setSettings(settingsRes.data.settings);
      setLocked(settingsRes.data.compliance_locked || []);
      setLangSettings(langRes.data.settings);
      setAvailableLangs(langRes.data.available_languages || []);
      setDirty(false);
    } catch { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const update = (key, val) => {
    setSettings(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  };

  const updateLang = (key, val) => {
    setLangSettings(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const [settingsRes] = await Promise.all([
        axios.put(`${API}/assistant-settings`, settings, { headers: hdrs }),
        langSettings ? axios.put(`${API}/language-settings`, langSettings, { headers: hdrs }) : Promise.resolve(),
      ]);
      setSettings(settingsRes.data.settings);
      setDirty(false);
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('Reset all settings to recommended defaults?')) return;
    try {
      const { data } = await axios.post(`${API}/assistant-settings/reset`, {}, { headers: hdrs });
      setSettings(data.settings);
      setDirty(false);
      toast.success('Settings restored to defaults');
    } catch { toast.error('Failed to reset'); }
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-5" data-testid="assistant-settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Assistant Settings</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Customize how your AI assistant sounds, qualifies leads, books appointments, and handles calls</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={reset}
            className="h-8 text-xs border-border text-muted-foreground hover:text-foreground gap-1.5"
            data-testid="reset-defaults-btn">
            <RotateCcw size={12} /> Restore Defaults
          </Button>
          <Button size="sm" onClick={save} disabled={saving || !dirty}
            className={`h-8 text-xs gap-1.5 ${dirty ? 'bg-emerald-600 hover:bg-emerald-500 text-white' : 'bg-muted text-muted-foreground cursor-default'}`}
            data-testid="save-settings-btn">
            {saving ? <><div className="h-3 w-3 rounded-full border-2 border-white border-t-transparent animate-spin" /> Saving...</>
              : dirty ? <><Save size={12} /> Save Changes</> : <><Check size={12} /> Saved</>}
          </Button>
        </div>
      </div>

      {/* Tab nav */}
      <div className="flex gap-1 overflow-x-auto border-b border-border pb-px" data-testid="settings-tabs">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-lg transition-colors whitespace-nowrap ${
              tab === t.id ? 'text-emerald-400 border-b-2 border-emerald-400 bg-card' : 'text-muted-foreground hover:text-foreground'
            }`} data-testid={`tab-${t.id}`}>
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="min-h-[400px]">
        {tab === 'industry' && <IndustryBrainSection />}
        {tab === 'identity' && <IdentityTab s={settings} u={update} />}
        {tab === 'voice' && <VoiceToneTab s={settings} u={update} />}
        {tab === 'language' && <LanguageTab langSettings={langSettings} updateLang={updateLang} available={availableLangs} agentName={settings?.agent_name} />}
        {tab === 'qualification' && <QualificationTab s={settings} u={update} />}
        {tab === 'booking' && <BookingTab s={settings} u={update} />}
        {tab === 'objection' && <ObjectionTab s={settings} u={update} />}
        {tab === 'escalation' && <EscalationTab s={settings} u={update} locked={locked} />}
        {tab === 'preview' && <PreviewTab token={token} />}
      </div>
    </div>
  );
}


// ── Identity Tab ────────────────────────────────────────────────────────

function IdentityTab({ s, u }) {
  return (
    <div className="space-y-4" data-testid="identity-tab">
      <Field label="Your Name" desc="Your display name used in greetings and introductions.">
        <input value={s.agent_name || ''} onChange={e => u('agent_name', e.target.value)}
          className="input-field" placeholder="e.g. Kevin Dole" data-testid="field-agent-name" />
      </Field>
      <Field label="AI Assistant Name" desc="Give your AI a name. This is how it introduces itself to callers.">
        <input value={s.assistant_name || ''} onChange={e => u('assistant_name', e.target.value)}
          className="input-field" placeholder="e.g. Rachael, Alex, Jordan" data-testid="field-assistant-name" />
        <p className="text-[11px] text-muted-foreground mt-1">Default: Rachael. Pick any name — male or female — to match the voice you choose.</p>
      </Field>
      <Field label="Brokerage Name" desc="Your brokerage, shown when relevant.">
        <input value={s.brokerage_name || ''} onChange={e => u('brokerage_name', e.target.value)}
          className="input-field" placeholder="e.g. ABC Realty" data-testid="field-brokerage-name" />
      </Field>
      <Field label="AI Disclosure Line" desc="Transparency statement. Use {agent_name} as a variable.">
        <input value={s.ai_disclosure || ''} onChange={e => u('ai_disclosure', e.target.value)}
          className="input-field" data-testid="field-ai-disclosure" />
      </Field>
      <Field label="Assistant Greeting" desc="What the AI says when answering. Use {agent_name}, {brokerage_text}, {assistant_name}.">
        <textarea value={s.greeting || ''} onChange={e => u('greeting', e.target.value)}
          rows={2} className="input-field resize-y" data-testid="field-greeting" />
      </Field>

      {/* Live preview */}
      <Card className="border-border bg-muted">
        <CardContent className="p-4">
          <p className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider mb-2 flex items-center gap-1"><Eye size={10} /> Greeting Preview</p>
          <p className="text-sm text-foreground italic" data-testid="greeting-preview">
            "{(s.greeting || '').replace('{agent_name}', s.agent_name || 'Agent').replace('{brokerage_text}', s.brokerage_name ? ` with ${s.brokerage_name}` : '').replace('{assistant_name}', s.assistant_name || 'Rachael')}"
          </p>
        </CardContent>
      </Card>
    </div>
  );
}


// ── Voice & Tone Tab ────────────────────────────────────────────────────

function VoiceToneTab({ s, u }) {
  return (
    <div className="space-y-5" data-testid="voice-tab">
      <OptionGrid label="Voice Gender" desc="Choose a male or female voice for your AI assistant."
        options={[
          { value: 'female', label: 'Female', desc: 'Warm, clear female voice' },
          { value: 'male', label: 'Male', desc: 'Confident, warm male voice' },
        ]}
        current={s.voice_gender || 'female'} onChange={v => u('voice_gender', v)} testId="voice-gender" />

      <OptionGrid label="Voice Style" desc="The overall sound quality of your AI assistant."
        options={[
          { value: 'standard', label: 'Standard', desc: 'Clear and reliable' },
          { value: 'human_like', label: 'Human-like', desc: 'Natural conversational flow' },
          { value: 'premium', label: 'Premium', desc: 'Ultra-smooth premium quality' },
        ]}
        current={s.voice_style} onChange={v => u('voice_style', v)} testId="voice-style" />

      <OptionGrid label="Tone" desc="The personality and warmth of responses."
        options={[
          { value: 'professional', label: 'Professional', desc: 'Polished and business-like' },
          { value: 'warm', label: 'Warm', desc: 'Approachable and caring' },
          { value: 'friendly', label: 'Friendly', desc: 'Casual and engaging' },
          { value: 'luxury', label: 'Luxury', desc: 'Refined and elegant' },
          { value: 'high_energy', label: 'High Energy', desc: 'Enthusiastic and upbeat' },
        ]}
        current={s.tone} onChange={v => u('tone', v)} testId="tone" />

      <OptionGrid label="Response Length" desc="How verbose the AI should be."
        options={[
          { value: 'concise', label: 'Concise', desc: 'Short and direct' },
          { value: 'balanced', label: 'Balanced', desc: 'Thorough but efficient' },
          { value: 'detailed', label: 'Detailed', desc: 'Comprehensive explanations' },
        ]}
        current={s.response_length} onChange={v => u('response_length', v)} testId="response-length" />

      {/* Voice summary */}
      <Card className="border-border bg-muted">
        <CardContent className="p-4">
          <p className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider mb-2 flex items-center gap-1"><Play size={10} /> Voice Summary</p>
          <p className="text-sm text-foreground">
            Your AI <span className="font-semibold">{s.assistant_name || 'Rachael'}</span> will use a{' '}
            <span className="font-semibold">{s.voice_gender || 'female'} {s.voice_style || 'standard'}</span> voice with a{' '}
            <span className="font-semibold">{s.tone || 'professional'}</span> tone.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}



// ── Language Tab ────────────────────────────────────────────────────────

function LanguageTab({ langSettings, updateLang, available, agentName }) {
  const ls = langSettings || {};
  const enabled = ls.enabled_languages || ['en', 'es'];
  const enabledSet = new Set(enabled);
  const allLangs = available || [];

  const toggleLang = (code) => {
    if (code === 'en') return; // English always required
    const next = enabledSet.has(code) ? enabled.filter(c => c !== code) : [...enabled, code];
    updateLang('enabled_languages', next);
  };

  const updateGreeting = (code, val) => {
    const greets = { ...(ls.per_language_greetings || {}) };
    greets[code] = val;
    updateLang('per_language_greetings', greets);
  };

  const updateDisclosure = (code, val) => {
    const discs = { ...(ls.per_language_disclosures || {}) };
    discs[code] = val;
    updateLang('per_language_disclosures', discs);
  };

  return (
    <div className="space-y-5" data-testid="language-tab">
      <p className="text-sm text-muted-foreground">Configure which languages your AI assistant supports and customize per-language greetings.</p>

      {/* Auto-detect toggle */}
      <Card className="border-border bg-card">
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-4 py-3.5 gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-foreground">Auto-detect caller language</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">AI will detect the caller's language from their first utterances and switch automatically.</p>
            </div>
            <Switch checked={ls.auto_detect_language ?? true} onCheckedChange={v => updateLang('auto_detect_language', v)}
              data-testid="toggle-auto-detect" />
          </div>
        </CardContent>
      </Card>

      {/* Default language */}
      <div>
        <p className="text-sm font-medium text-foreground mb-0.5">Default Language</p>
        <p className="text-[11px] text-muted-foreground mb-3">The language the AI starts with before detecting the caller's preference.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {allLangs.filter(l => enabledSet.has(l.code)).map(l => (
            <button key={l.code} onClick={() => updateLang('default_language', l.code)}
              className={`rounded-lg border p-3 text-left transition-all ${
                (ls.default_language || 'en') === l.code
                  ? 'bg-emerald-500/10 border-emerald-500/30 ring-1 ring-emerald-500/20'
                  : 'bg-card border-border hover:border-border'
              }`} data-testid={`default-lang-${l.code}`}>
              <p className={`text-xs font-medium ${(ls.default_language || 'en') === l.code ? 'text-emerald-400' : 'text-foreground'}`}>{l.display_name}</p>
              <p className="text-[10px] text-muted-foreground">{l.code.toUpperCase()}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Enabled languages */}
      <div>
        <p className="text-sm font-medium text-foreground mb-0.5">Enabled Languages</p>
        <p className="text-[11px] text-muted-foreground mb-3">Select which languages your AI assistant can handle. English is always required.</p>
        <Card className="border-border bg-card">
          <CardContent className="p-0 divide-y divide-slate-800/50">
            {allLangs.map(l => (
              <div key={l.code} className="flex items-center justify-between px-4 py-3 gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground flex items-center gap-1.5">
                    {l.display_name}
                    <Badge className="text-[9px] bg-muted text-muted-foreground">{l.code.toUpperCase()}</Badge>
                    {l.code === 'en' && <Lock size={10} className="text-muted-foreground" />}
                    {!l.enabled && <Badge className="text-[9px] bg-amber-500/10 text-amber-400 border-amber-500/20">Coming Soon</Badge>}
                  </p>
                </div>
                <Switch checked={enabledSet.has(l.code)} onCheckedChange={() => toggleLang(l.code)}
                  disabled={l.code === 'en' || !l.enabled}
                  data-testid={`toggle-lang-${l.code}`} />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Per-language greetings */}
      {enabled.length > 0 && (
        <div>
          <p className="text-sm font-medium text-foreground mb-0.5">Per-Language Greetings</p>
          <p className="text-[11px] text-muted-foreground mb-3">Customize how the AI greets callers in each language. Leave blank for defaults.</p>
          <div className="space-y-3">
            {enabled.filter(c => allLangs.some(l => l.code === c && l.enabled)).map(code => {
              const lang = allLangs.find(l => l.code === code);
              const placeholder = code === 'es'
                ? `Gracias por llamar. Soy el asistente de ${agentName || 'Agent'}. ¿En qué puedo ayudarle?`
                : `Hi, this is the AI assistant for ${agentName || 'Agent'}. How can I help you?`;
              return (
                <div key={code} className="space-y-1" data-testid={`greeting-${code}`}>
                  <label className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Globe size={10} /> {lang?.display_name || code} Greeting
                  </label>
                  <textarea value={(ls.per_language_greetings || {})[code] || ''} onChange={e => updateGreeting(code, e.target.value)}
                    rows={2} className="input-field resize-y text-xs" placeholder={placeholder} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Per-language disclosures */}
      {enabled.length > 0 && (
        <div>
          <p className="text-sm font-medium text-foreground mb-0.5">Per-Language AI Disclosure</p>
          <p className="text-[11px] text-muted-foreground mb-3">The transparency statement in each language. Leave blank for defaults.</p>
          <div className="space-y-3">
            {enabled.filter(c => allLangs.some(l => l.code === c && l.enabled)).map(code => {
              const lang = allLangs.find(l => l.code === code);
              const placeholder = code === 'es'
                ? 'Soy un asistente de inteligencia artificial que trabaja en nombre del agente.'
                : "I'm an AI assistant helping on behalf of the agent.";
              return (
                <div key={code} className="space-y-1" data-testid={`disclosure-${code}`}>
                  <label className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Shield size={10} /> {lang?.display_name || code} Disclosure
                  </label>
                  <input value={(ls.per_language_disclosures || {})[code] || ''} onChange={e => updateDisclosure(code, e.target.value)}
                    className="input-field text-xs" placeholder={placeholder} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


// ── Lead Qualification Tab ──────────────────────────────────────────────

function QualificationTab({ s, u }) {
  const toggles = [
    { key: 'ask_buyer_budget', label: 'Ask buyer budget / price range' },
    { key: 'ask_preferred_area', label: 'Ask preferred area / neighborhood' },
    { key: 'ask_bedrooms_bathrooms', label: 'Ask bedrooms & bathrooms' },
    { key: 'ask_timeline', label: 'Ask buying / moving timeline' },
    { key: 'ask_financing', label: 'Ask about financing / pre-approval' },
    { key: 'ask_seller_address', label: 'Ask seller property address' },
    { key: 'ask_seller_timeline', label: 'Ask seller timeline' },
    { key: 'ask_reason_for_selling', label: 'Ask reason for selling' },
  ];

  return (
    <div className="space-y-4" data-testid="qualification-tab">
      <p className="text-sm text-muted-foreground">Control which questions the AI asks to qualify leads during calls.</p>

      <OptionGrid label="Qualification Mode" desc="Should the AI require answers or treat questions as optional?"
        options={[
          { value: 'required', label: 'Required', desc: 'AI will push for all enabled questions' },
          { value: 'optional', label: 'Optional', desc: 'AI asks naturally, skips if caller deflects' },
        ]}
        current={s.qualification_mode} onChange={v => u('qualification_mode', v)} testId="qual-mode" />

      <Card className="border-border bg-card">
        <CardContent className="p-0 divide-y divide-slate-800/50">
          {toggles.map(t => (
            <ToggleRow key={t.key} id={t.key} label={t.label}
              checked={s[t.key] ?? true} onChange={v => u(t.key, v)} />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}


// ── Booking Rules Tab ───────────────────────────────────────────────────

function BookingTab({ s, u }) {
  return (
    <div className="space-y-5" data-testid="booking-tab">
      <OptionGrid label="Primary CTA" desc="What should the AI try to book?"
        options={[
          { value: 'consultation', label: 'Consultation', desc: 'Free consultation call' },
          { value: 'showing', label: 'Showing', desc: 'Property showing or tour' },
          { value: 'callback', label: 'Callback', desc: 'Agent callback request' },
        ]}
        current={s.primary_cta} onChange={v => u('primary_cta', v)} testId="primary-cta" />

      <Card className="border-border bg-card">
        <CardContent className="p-0 divide-y divide-slate-800/50">
          <ToggleRow id="offer_booking_on_high_intent" label="Offer booking when high intent is detected" desc="AI automatically suggests booking when caller shows strong buying signals."
            checked={s.offer_booking_on_high_intent ?? true} onChange={v => u('offer_booking_on_high_intent', v)} />
          <ToggleRow id="allow_direct_booking" label="Allow AI to book directly" desc="If disabled, AI will only request a callback instead of booking."
            checked={s.allow_direct_booking ?? true} onChange={v => u('allow_direct_booking', v)} />
        </CardContent>
      </Card>

      <div className="grid sm:grid-cols-3 gap-4">
        <NumberField label="Max booking attempts" desc="Per call" value={s.max_booking_attempts ?? 2}
          onChange={v => u('max_booking_attempts', v)} min={1} max={5} testId="max-attempts" />
        <NumberField label="Buffer time" desc="Minutes between appointments" value={s.buffer_minutes ?? 30}
          onChange={v => u('buffer_minutes', v)} min={0} max={120} step={15} testId="buffer-time" />
        <NumberField label="Default duration" desc="Minutes per appointment" value={s.default_duration_minutes ?? 30}
          onChange={v => u('default_duration_minutes', v)} min={15} max={120} step={15} testId="default-duration" />
      </div>
    </div>
  );
}


// ── Objection Handling Tab ──────────────────────────────────────────────

function ObjectionTab({ s, u }) {
  const examples = {
    soft: [
      { q: '"Just browsing"', a: 'I understand! No pressure at all. Feel free to reach out whenever you\'re ready.' },
      { q: '"Not ready yet"', a: 'That\'s perfectly fine. Would you like me to keep you updated on new listings in your area?' },
      { q: '"Send me info"', a: 'Of course! I\'ll have some information sent right over.' },
    ],
    balanced: [
      { q: '"Just browsing"', a: 'No problem! Would it help if I scheduled a quick consultation at a time that works for you?' },
      { q: '"Not ready yet"', a: 'Understood. The market moves fast though — would a brief call help you stay ahead?' },
      { q: '"Send me info"', a: 'Absolutely. Would you also like a quick call to go over the highlights?' },
    ],
    assertive: [
      { q: '"Just browsing"', a: 'The market moves quickly — I\'d love to set up a quick showing so you don\'t miss out. What day works?' },
      { q: '"Not ready yet"', a: 'I hear you. But getting pre-qualified now means you\'re ready when the right home appears. Can we set that up?' },
      { q: '"Send me info"', a: 'I\'ll send that right over. While I have you, let me also book a quick call to walk you through the best options.' },
    ],
  };

  const style = s.objection_style || 'balanced';

  return (
    <div className="space-y-5" data-testid="objection-tab">
      <OptionGrid label="Objection Style" desc="How aggressively should the AI handle pushback?"
        options={[
          { value: 'soft', label: 'Soft', desc: 'Gentle, no pressure' },
          { value: 'balanced', label: 'Balanced', desc: 'Respectful with a nudge' },
          { value: 'assertive', label: 'Assertive', desc: 'Confident, action-oriented' },
        ]}
        current={style} onChange={v => u('objection_style', v)} testId="objection-style" />

      <OptionGrid label="After 2 Declines" desc="What should the AI do if the caller declines twice?"
        options={[
          { value: 'offer_callback', label: 'Offer Callback', desc: 'Suggest a callback at a better time' },
          { value: 'offer_sms', label: 'Offer SMS Follow-up', desc: 'Send a text with info' },
          { value: 'stop', label: 'Stop Pushing', desc: 'Respect the decline and end gracefully' },
        ]}
        current={s.after_decline_action} onChange={v => u('after_decline_action', v)} testId="decline-action" />

      {/* Example previews */}
      <Card className="border-border bg-muted">
        <CardContent className="p-4">
          <p className="text-[10px] font-semibold text-violet-400 uppercase tracking-wider mb-3 flex items-center gap-1"><MessageCircle size={10} /> Example Responses ({style})</p>
          <div className="space-y-3">
            {(examples[style] || examples.balanced).map((ex, i) => (
              <div key={i} className="space-y-1" data-testid={`objection-example-${i}`}>
                <p className="text-xs text-muted-foreground">Caller: <span className="text-foreground">{ex.q}</span></p>
                <p className="text-xs text-emerald-400/80 italic pl-3 border-l-2 border-emerald-500/20">{ex.a}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


// ── Escalation & Compliance Tab ─────────────────────────────────────────

function EscalationTab({ s, u, locked }) {
  const toggles = [
    { key: 'transfer_on_request', label: 'Transfer to agent on direct request', desc: 'Immediately connects caller to you when they ask for the agent.' },
    { key: 'escalate_legal_questions', label: 'Escalate legal/contract questions', desc: 'AI will not attempt to answer legal questions — transfers immediately.', locked: true },
    { key: 'escalate_upset_callers', label: 'Escalate upset callers', desc: 'Transfer to you when caller is frustrated or confrontational.' },
    { key: 'escalate_offer_ready', label: 'Escalate offer-ready leads', desc: 'Immediately notify when a caller is ready to make an offer.' },
    { key: 'fair_housing_mode', label: 'Enforce Fair Housing safe mode', desc: 'Prevents AI from making discriminatory statements. Cannot be disabled.', locked: true },
    { key: 'require_ai_disclosure', label: 'Require AI disclosure in first interaction', desc: 'AI must identify itself as an AI assistant. Cannot be disabled.', locked: true },
  ];

  return (
    <div className="space-y-4" data-testid="escalation-tab">
      <div className="bg-amber-500/5 border border-amber-500/15 rounded-lg p-3 flex items-start gap-2">
        <AlertTriangle size={14} className="text-amber-400 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-300/80">Agent9 is an AI assistant and should not provide legal, tax, or contract advice. Some compliance settings are locked for safety.</p>
      </div>

      <Card className="border-border bg-card">
        <CardContent className="p-0 divide-y divide-slate-800/50">
          {toggles.map(t => (
            <ToggleRow key={t.key} id={t.key} label={t.label} desc={t.desc}
              checked={s[t.key] ?? true}
              onChange={v => { if (!t.locked) u(t.key, v); }}
              locked={t.locked} />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}


// ── Preview / Test Tab ──────────────────────────────────────────────────

function PreviewTab({ token }) {
  const [greetingPreview, setGreetingPreview] = useState(null);
  const [examples, setExamples] = useState(null);
  const [loadingG, setLoadingG] = useState(false);
  const [loadingE, setLoadingE] = useState(false);
  const hdrs = { Authorization: `Bearer ${token}` };

  const loadGreeting = async () => {
    setLoadingG(true);
    try {
      const { data } = await axios.post(`${API}/assistant-settings/preview-greeting`, {}, { headers: hdrs });
      setGreetingPreview(data);
    } catch { toast.error('Failed to preview greeting'); }
    finally { setLoadingG(false); }
  };

  const loadExamples = async () => {
    setLoadingE(true);
    try {
      const { data } = await axios.post(`${API}/assistant-settings/test-response`, {}, { headers: hdrs });
      setExamples(data);
    } catch { toast.error('Failed to generate examples'); }
    finally { setLoadingE(false); }
  };

  return (
    <div className="space-y-5" data-testid="preview-tab">
      <p className="text-sm text-muted-foreground">See how your current settings affect your AI assistant's responses.</p>

      {/* Greeting preview */}
      <Card className="border-border bg-card">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-foreground">Greeting Preview</p>
            <Button size="sm" onClick={loadGreeting} disabled={loadingG}
              className="h-7 text-[10px] bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30 gap-1"
              data-testid="preview-greeting-btn">
              {loadingG ? 'Loading...' : <><Play size={10} /> Test Greeting</>}
            </Button>
          </div>
          {greetingPreview && (
            <div className="space-y-2 animate-in fade-in" data-testid="greeting-result">
              <div className="bg-emerald-500/5 border border-emerald-500/15 rounded-lg p-3">
                <p className="text-xs text-emerald-400/80 italic">"{greetingPreview.greeting}"</p>
              </div>
              <div className="bg-muted rounded-lg p-3">
                <p className="text-[10px] text-muted-foreground mb-1">AI Disclosure:</p>
                <p className="text-xs text-foreground italic">"{greetingPreview.disclosure}"</p>
              </div>
              <div className="flex gap-2">
                <Badge className="text-[10px] bg-muted text-foreground">Tone: {greetingPreview.tone}</Badge>
                <Badge className="text-[10px] bg-muted text-foreground">Length: {greetingPreview.response_length}</Badge>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Response examples */}
      <Card className="border-border bg-card">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-foreground">Response Examples</p>
            <Button size="sm" onClick={loadExamples} disabled={loadingE}
              className="h-7 text-[10px] bg-violet-600/20 text-violet-400 hover:bg-violet-600/30 gap-1"
              data-testid="test-responses-btn">
              {loadingE ? 'Loading...' : <><Eye size={10} /> Generate Examples</>}
            </Button>
          </div>
          {examples && (
            <div className="space-y-3 animate-in fade-in" data-testid="examples-result">
              {examples.examples.map((ex, i) => (
                <div key={i} className="bg-muted rounded-lg p-3" data-testid={`example-${i}`}>
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">{ex.scenario}</p>
                  <p className="text-xs text-foreground italic">"{ex.response}"</p>
                </div>
              ))}
              <div className="flex gap-2">
                {Object.entries(examples.settings_summary).map(([k, v]) => (
                  <Badge key={k} className="text-[10px] bg-muted text-foreground">{k.replace(/_/g, ' ')}: {v}</Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


// ── Shared Components ───────────────────────────────────────────────────

function Field({ label, desc, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-0.5">{label}</label>
      {desc && <p className="text-[11px] text-muted-foreground mb-2">{desc}</p>}
      {children}
    </div>
  );
}

function ToggleRow({ id, label, desc, checked, onChange, locked }) {
  return (
    <div className="flex items-center justify-between px-4 py-3.5 gap-3">
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground flex items-center gap-1.5">
          {label}
          {locked && <Lock size={10} className="text-amber-400" />}
        </p>
        {desc && <p className="text-[11px] text-muted-foreground mt-0.5">{desc}</p>}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} disabled={locked}
        data-testid={`toggle-${id}`} />
    </div>
  );
}

function OptionGrid({ label, desc, options, current, onChange, testId }) {
  return (
    <div>
      <p className="text-sm font-medium text-foreground mb-0.5">{label}</p>
      {desc && <p className="text-[11px] text-muted-foreground mb-3">{desc}</p>}
      <div className={`grid gap-2 ${options.length <= 3 ? 'grid-cols-3' : options.length <= 4 ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5'}`}>
        {options.map(o => (
          <button key={o.value} onClick={() => onChange(o.value)}
            className={`rounded-lg border p-3 text-left transition-all ${
              current === o.value
                ? 'bg-emerald-500/10 border-emerald-500/30 ring-1 ring-emerald-500/20'
                : 'bg-card border-border hover:border-border'
            }`} data-testid={`${testId}-${o.value}`}>
            <p className={`text-xs font-medium ${current === o.value ? 'text-emerald-400' : 'text-foreground'}`}>{o.label}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">{o.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function NumberField({ label, desc, value, onChange, min, max, step = 1, testId }) {
  return (
    <Card className="border-border bg-card">
      <CardContent className="p-4">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-[10px] text-muted-foreground mb-2">{desc}</p>
        <input type="number" value={value} onChange={e => onChange(Number(e.target.value))}
          min={min} max={max} step={step}
          className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-emerald-500"
          data-testid={testId} />
      </CardContent>
    </Card>
  );
}
